// React
import { useCallback, useMemo, useRef } from 'react';

export interface IRequestSaverFunctions {
  requestSaverAbortAll: () => void;
  requestSaverAbort: (id: string) => void;
  requestSaverSetCancellation: (id: string) => { signal: AbortSignal };
}

export function useRequestSaver(): IRequestSaverFunctions {
  const requestSaverList = useRef<Record<string, AbortController>>({});

  const requestSaverAbortAll = useCallback(() => {
    if (Object.keys(requestSaverList.current).length > 0) {
      // Abort incoming request
      Object.values(requestSaverList.current).forEach(requestSaver => {
        if (!requestSaver.signal.aborted) requestSaver.abort();
      });

      // Clear all request from state
      requestSaverList.current = {};
    }
  }, []);

  const requestSaverAbort = useCallback((id: string) => {
    const request = requestSaverList.current?.[id];

    // Check if request exists
    // Check if request is not aborted yet, if not aborted, abort it
    // In the end, clear the aborted request
    if (request && !request.signal.aborted) {
      // Abort the request
      request.abort();

      // Delete aborted request
      delete requestSaverList.current[id];
    }
  }, []);

  const requestSaverSetCancellation = useCallback((id: string) => {
    // Check if theres any request with the same identifier
    // Abort the request if exists
    if (requestSaverList.current?.[id]) requestSaverAbort(id);

    // Make instance of AbortController
    const abortController = new AbortController();

    // Make signal from abort controller to list
    requestSaverList.current[id] = abortController;

    // Return saved signal that stored to state
    return { signal: abortController.signal };
  }, []);

  const hookData = useMemo(
    () => ({ requestSaverAbortAll, requestSaverAbort, requestSaverSetCancellation }),
    [requestSaverAbortAll, requestSaverAbort, requestSaverSetCancellation],
  );

  return hookData;
}
